import type { MediaItemType } from '~/utils/musickit/index';
import type { MediaItem } from '@amp/musickit';
import {
    makePlayQueueItemAction,
    makeRemoveQueueItemAction,
} from '~/actions/playback-actions';
import { get } from 'svelte/store';
import { queueMetadata } from '~/stores/queue-metadata';

export type { MediaItem } from '@amp/musickit';

import { createPlaybackQueueStore as sharedCreatePlaybackQueueStore } from '@amp/web-apps-common';
import type {
    IndexedMusicKitQueueItem as SharedIndexedMusicKitQueueItem,
    PlaybackQueueStore as SharedPlayBackQueueStore,
} from '@amp/web-apps-common';
import type { QueueItemMetadata } from '~/jet/models';

export type IndexedMusicKitQueueItem =
    SharedIndexedMusicKitQueueItem<MediaItem>;
export interface PlaybackQueueStore
    extends SharedPlayBackQueueStore<MediaItemType, MediaItem> {}

// This store is being used for the playback state, that changes when song or play/pause changed
export const createPlaybackQueueStore = () =>
    sharedCreatePlaybackQueueStore<MediaItemType, MediaItem>();

export type PlaybackQueue = PlaybackQueueStore['external'];

/**
 * Utility that fetches the Classical queue metadata for a given queue item
 * from the metadata store and decorates the item with queue actions.
 *
 * Passing in the entire media item from MK for now, in the event that we may need to
 * access certain properties from it in the future.  The object from MK has some
 * Classical context to it, but any urls for the song/album itself point to Music.
 * Some formatting of work/movement/track/artist names may differ as well.
 *
 * The metadata accessed from the store is fetched from the Classical API and should
 * be used first before relying on the MK data.
 *
 * @function mediaItemtoQueueItem
 * @param {MediaItem} mediaItem Queue item data provided by MusicKit
 * @param {number} index Queue index of item
 * @returns {QueueItemMetadata}
 */
export function mediaItemToQueueItem(
    mediaItem: MediaItem,
    index: number,
): QueueItemMetadata {
    const queueItemId = mediaItem.id;
    const id = mediaItem.attributes?.playParams?.catalogId || queueItemId;
    const queueItemMetadata: QueueItemMetadata = get(queueMetadata)?.get(id);

    return {
        ...queueItemMetadata,
        // while the queue item metadata expects the catalog id, changing
        // the active queue item requires the id of that song, be it the
        // library or catalog id
        playAction: makePlayQueueItemAction(queueItemId, index),
        removeFromQueueAction: makeRemoveQueueItemAction(queueItemId, index),
    };
}
